import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryLoader from "@/components/dictionaries/DictionaryLoader";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { DictionaryContext } from "@/containers/dictionaries/types";
import { useFormResetWithQueryData } from "@/hooks/useFormResetWithQueryData";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import PayrollTypeForm from "@/modules/payrollTypes/components/PayrollTypeForm";
import PayrollTypeDeleteDialog from "@/modules/payrollTypes/components/PayrollTypeDeleteDialog";
import PayrollTypeEditorFooter from "@/modules/payrollTypes/components/PayrollTypeEditorFooter";
import { usePayrollTypesDictionary } from "@/modules/payrollTypes/hooks/usePayrollTypesDictionary";
import { updatePayrollType } from "@/schemas/payrollType";
import { ApiError } from "@/services/api";
import {
  ReadPayrollType,
  UpdatePayrollType,
  WritePayrollType,
} from "@/types/payrollType";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { exists } from "i18next";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

export default function PayrollTypeEditor({ id }: DictionaryContext) {
  const { t } = useTranslation();
  const config = usePayrollTypesDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const labels = config.getLabels(t);
  const { updateQueryDataForExistingItem } = useQueryCacheUpdater(
    Module.PayrollTypes,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { can_update, can_delete } = useModulePermissions(Module.PayrollTypes);

  const getQuery = useQuery({
    queryKey: [config.ns, config.id, id],
    queryFn: async () => config.service.get(id),
  });

  const updateMutation = useMutation<
    ReadPayrollType,
    ApiError,
    UpdatePayrollType
  >({
    mutationFn: (values) => config.service.patch(id, values),
    onSuccess: (response) => {
      const id = response.id;
      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.update,
      });
      updateQueryDataForExistingItem(response);
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.update,
        description,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => config.service.delete(id),
    onSuccess: () => {
      setDeleteDialogOpen(false);
      navigate(config.path);
      toast({
        title: labels.success.delete,
      });
    },
    onError: () => {
      toast({
        title: labels.errors.delete,
        variant: "destructive",
      });
    },
  });

  const form = useForm<WritePayrollType>({
    resolver: zodResolver(updatePayrollType),
    defaultValues: config.defaultValues,
    disabled:
      updateMutation.isPending || deleteMutation.isPending || !can_update,
  });

  useFormUnsavedChangesWarning(form);
  useFormResetWithQueryData(form, getQuery);

  const handleSave = form.handleSubmit((data) => {
    updateMutation.mutate(data);
  });

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  return (
    <>
      <DictionaryRecordContextProvider
        value={{
          query: getQuery,
          config,
          mutation: updateMutation,
          deleteMutation,
          form,
          id,
        }}
      >
        <DictionaryPageHeader />
        {getQuery.isLoading ? (
          <DictionaryLoader />
        ) : (
          <PayrollTypeForm
            mode="update"
            schema={updatePayrollType}
            footer={
              <PayrollTypeEditorFooter
                onSave={handleSave}
                onDelete={handleDelete}
                isPending={updateMutation.isPending || deleteMutation.isPending}
                can_update={can_update}
                can_delete={can_delete}
              />
            }
          />
        )}
      </DictionaryRecordContextProvider>
      <PayrollTypeDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        payrollTypeName={getQuery.data?.name || ""}
        deleteMutation={deleteMutation}
      />
    </>
  );
}
